import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useUser } from '@/utils/hooks'
import { ApproveCommentModal } from '../ApproveCommentModal'
import type { WorkPeriodNode } from '../types.graphql'
import { useWorkPeriodApprovePermissions } from '../hooks'
import { ApprovalColumn } from './ApprovalColumn'

export type ApprovalPatchValues = Partial<
  Pick<
    WorkPeriodNode,
    'approvedByUser' | 'approvedByManager' | 'userComment' | 'managerComment'
  >
>

interface Props {
  workPeriod: WorkPeriodNode
  patchWorkPeriod: (id: string, values: ApprovalPatchValues) => void
  driverApprove?: boolean
  managerApprove?: boolean
  onModalOpen?: () => void
  onModalClose?: () => void
}

export const WorkPeriodApprovalBoxes: React.FC<Props> = ({
  workPeriod,
  patchWorkPeriod,
  driverApprove = true,
  managerApprove = true,
  onModalOpen,
  onModalClose,
}) => {
  const me = useUser()
  const addPrompt = usePrompt()
  const translations = useTranslate({
    approvedByDriver: 'common.approved-by-driver',
    approvedByManager: 'common.approved-by-manager',
    lockedByManager: 'salary.locked-by-manager-approval',
    noPermissionToChange: 'salary.no-permission-to-change',
  })

  const { workPeriodApprovalState } = useWorkPeriodApprovePermissions()
  const { canApproveAsDriver, canApproveAsManager } = workPeriodApprovalState(
    me.id,
    workPeriod
  )

  const promptComment = async (
    existingComment: string,
    existingApproved: boolean
  ) => {
    onModalOpen?.()
    const { data } = await addPrompt<{ comment: string } | null>(resolve => (
      <ApproveCommentModal
        existingComment={existingComment}
        existingApproved={existingApproved}
        onSubmit={resolve}
      />
    ))
    onModalClose?.()
    return data
  }

  const handleToggle = async (side: 'driver' | 'manager') => {
    const isDriver = side === 'driver'
    const currentlyApproved = isDriver
      ? workPeriod.approvedByUser
      : workPeriod.approvedByManager
    const nextApproved = !currentlyApproved
    const existingComment = isDriver
      ? workPeriod.userComment
      : workPeriod.managerComment

    if (!nextApproved) {
      patchWorkPeriod(
        workPeriod.id,
        isDriver ? { approvedByUser: false } : { approvedByManager: false }
      )
      return
    }

    const data = await promptComment(existingComment, nextApproved)
    if (!data) return
    patchWorkPeriod(
      workPeriod.id,
      isDriver
        ? { approvedByUser: true, userComment: data.comment }
        : { approvedByManager: true, managerComment: data.comment }
    )
  }

  return (
    <div className="flex w-full flex-row gap-4">
      {driverApprove && (
        <ApprovalColumn
          label={translations.approvedByDriver}
          approved={workPeriod.approvedByUser}
          comment={workPeriod.userComment}
          approverName={workPeriod.userApprover?.fullName}
          locked={!canApproveAsDriver}
          lockText={translations.lockedByManager}
          onToggle={() => void handleToggle('driver')}
        />
      )}
      {managerApprove && (
        <ApprovalColumn
          label={translations.approvedByManager}
          approved={workPeriod.approvedByManager}
          comment={workPeriod.managerComment}
          approverName={workPeriod.managerApprover?.fullName}
          locked={!canApproveAsManager}
          lockText={translations.noPermissionToChange}
          onToggle={() => void handleToggle('manager')}
        />
      )}
    </div>
  )
}
